import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import TeachrRepository from '../Repositories/TeachrRepository';
import { DispatchClientParams } from '../config/CLIENT_TYPE_CONFIG';
import config from '../config/config';
import { ENV_TEACHR, isBrandActive } from '../config/environment.config';

// Seuil en dessous duquel on propose au prof l'accompagnement partenaire
// pour développer son activité (aligné sur la question posée à l'inscription).
export const PARTNER_ADVICE_STUDENT_THRESHOLD = 3;

// Durée pendant laquelle la bannière reste masquée après un "Plus tard" (7 jours)
const SNOOZE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const SNOOZE_STORAGE_KEY = 'partnerAdviceSnoozedUntil';

const isSnoozed = (): boolean => {
    try {
        const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
        return !!raw && Number(raw) > Date.now();
    } catch {
        return false;
    }
};

/**
 * Détermine si on doit proposer au prof la page partenaire (config.partnerAdviceUrl)
 * pour l'aider à trouver plus d'élèves.
 *
 * Éligible : marque Teach'r uniquement, et moins de PARTNER_ADVICE_STUDENT_THRESHOLD
 * élèves (actifs + en attente) d'après les données réelles de son compte, avec repli
 * sur le nombre déclaré à l'inscription tant que les données ne sont pas chargées.
 */
const usePartnerAdvice = () => {
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const teachrId: number | undefined = teachr?.id;
    const isTeachrBrand = isBrandActive(ENV_TEACHR);
    const [snoozed, setSnoozed] = useState<boolean>(isSnoozed);

    const dataObject: DispatchClientParams = useMemo(() => ({
        teachrId: teachrId as number,
        archive: 0,
        page: 0,
        variable: 0,
        sort: 1,
        limit: 10,
        validated: 1,
        search: '',
    }), [teachrId]);

    // Même queryKey que le tableau de bord : la requête est partagée/dédupliquée
    const { data: clientsData } = useQuery({
        queryKey: ['allClients', teachrId],
        queryFn: () => TeachrRepository.getAllClientsForTeachr(dataObject),
        enabled: !!teachrId && isTeachrBrand,
    });

    const studentCount: number | undefined = useMemo(() => {
        if (clientsData) {
            return (clientsData.totalValidated ?? 0) + (clientsData.totalNotValidated ?? 0);
        }
        if (typeof teachr?.numberOfStudents === 'number') {
            return teachr.numberOfStudents;
        }
        return undefined;
    }, [clientsData, teachr?.numberOfStudents]);

    const isEligible = isTeachrBrand && studentCount !== undefined && studentCount < PARTNER_ADVICE_STUDENT_THRESHOLD;

    const snooze = useCallback(() => {
        try {
            localStorage.setItem(SNOOZE_STORAGE_KEY, String(Date.now() + SNOOZE_DURATION_MS));
        } catch {
            // stockage indisponible : on masque uniquement pour la session
        }
        setSnoozed(true);
    }, []);

    const openPartnerPage = useCallback(() => {
        window.open(config.partnerAdviceUrl, '_blank', 'noopener,noreferrer');
    }, []);

    return {
        visible: isEligible && !snoozed,
        studentCount: studentCount ?? 0,
        snooze,
        openPartnerPage,
    };
};

export default usePartnerAdvice;
